<template>
  <div>
    <EditSourceDialog ref="editSourceDialog" />
    <v-card-actions class="card-bg-1 py- layout wrap">
      <AutoAssignDialog :selectionCounter="selectionCounter" :selected="selected" :items="items" />
    </v-card-actions>
    <v-divider />
    <v-layout class="align-baseline py-2">
      <v-item-group mandatory v-model="filterToggle" active-class="white--text">
        <v-subheader style="display: inline; cursor: default;">Show:</v-subheader>
        <v-item>
          <v-chip small slot-scope="{ active, toggle }" :color="active ? 'primary' : 'default'" :selected="active" class="chipFilter clickable" @click="toggle">All</v-chip>
        </v-item>
        <v-item>
          <v-chip small slot-scope="{ active, toggle }" :color="active ? 'primary' : 'default'" :selected="active" class="chipFilter clickable" @click="toggle">
            <div style="float: left;" class="pr-2" :class="{ 'error--text font-weight-bold': unassignedLength > 0 }">
              {{ unassignedLength }}</div>
            Unassigned
          </v-chip>
        </v-item>
      </v-item-group>
      <v-spacer />
      <v-text-field class="rounded emphasize-clear" prepend-inner-icon="search" v-model="search" clearable solo placeholder="Search data sources" hide-details />
    </v-layout>
    <v-divider />
    <v-data-table :headers="headers" :items="filteredItems" :pagination.sync="pagination" :search="search" class="elevation-">
      <template v-slot:headers="props">
        <tr>
          <th>
            <v-checkbox color="primary" :input-value="hasEvery" :indeterminate="hasSome" primary hide-details @click.stop="toggleAll()"></v-checkbox>
          </th>
          <th v-for="header in props.headers" :key="header.text" :class="[
              'column', header.sortable && 'sortable',
              pagination.descending ? 'desc' : 'asc',
              header.value === pagination.sortBy ? 'active' : '', `text-xs-${header.align}`
            ]" @click="changeSort(header.sortable, header.value)">
            <v-icon small v-if="header.sortable">arrow_upward</v-icon>
            {{ header.text }}
          </th>
        </tr>
      </template>
      <template v-slot:items="{ item }">
        <td>
          <v-checkbox @click.stop="toggleRow(item)" :input-value="selected[item.id]" color="primary" class="clickable" primary hide-details />
        </td>
        <td>
          <strong>{{ item.LocationIdentifier }}</strong>
        </td>
        <td>
          <template v-if="item.MonitoringFeatureId">
            <v-icon small>open_in_new</v-icon>
            <router-link target="_blank" :to="{ name: 'monitoringfeature', params: { monitoringFeatureId: item.MonitoringFeatureId }}">
              {{ item.locationName }}
            </router-link>
          </template>
          <v-chip v-else outline color="error" small label>
            Unassigned
          </v-chip>
        </td>
        <td>
          {{ item.dataType }}
        </td>
        <td class="text-xs-center">
          <v-tooltip :open-delay="500" left>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" icon @click="editItem(item)">
                <v-icon color="warning">edit</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("Button.Edit") }}</span>
          </v-tooltip>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
  import {
    createNamespacedHelpers,
  } from 'vuex'

  const dataSources = createNamespacedHelpers('datasource')
  const monitoringFeatures = createNamespacedHelpers('monitoringfeature')

  import {
    SOURCE_TYPES,
    SOURCE_TYPES_WITH_NAME_INDEX,
  } from '@/store/MonitoringFeature/monitoringfeature'

  import AutoAssignDialog from './AutoAssignDialog'
  import EditSourceDialog from './EditSourceDialog'

  const ID_KEY = 'id'
  const makeAll = (items, value) => {
    let index = {}
    items.forEach(item => index[item.id] = value)
    return index
  }

  export default {
    components: {
      AutoAssignDialog,
      EditSourceDialog,
    },
    props: {
      unassignedLength: Number,
    },
    data: () => ({
      search: '',
      selectionKey: ID_KEY,
      selectionCounter: 0,
      filterToggle: 0,
      selected: {},
      pagination: {
        sortBy: 'LocationIdentifier',
        rowsPerPage: 10,
        page: 1,
      },
      headers: [{
        text: 'Source Name',
        align: 'left',
        value: 'LocationIdentifier',
        sortable: true,
      },
      {
        text: 'Location Name',
        align: 'left',
        value: 'locationName',
        sortable: true,
      },
      {
        text: 'Data Type',
        align: 'left',
        value: 'dataType',
        sortable: true,
      },
      {
        text: 'Actions',
        align: 'center',
        value: 'actions',
        sortable: false,
      },
      ],
    }),
    computed: {
      ...monitoringFeatures.mapGetters([
        'monitoringFeatures',
        'getDisplayNameById',
      ]),
      ...dataSources.mapGetters(['dataSources', 'dataSourceIndexByMF']),
      items() {
        let items = []

        let index = 0
        Object.values(SOURCE_TYPES).forEach(sourceType => {
          let dataSources = this.dataSources[sourceType]
          if (!dataSources) return
          let dsItems = dataSources.map((ds) => ({
            ...ds,
            id: index++,
            sourceId: ds.Id,
            locationName: this.getDisplayNameById(ds.MonitoringFeatureId),
            dataType: SOURCE_TYPES_WITH_NAME_INDEX[sourceType].dataType,
            sourceType: sourceType,
          }))

          items.push(...dsItems)
        })

        return items
      },
      unassignedItems() {
        return this.items.filter(item => !item.MonitoringFeatureId)
      },
      filteredItems() {
        return this.filterToggle == 0 ? this.items : this.unassignedItems
      },
      hasSome() {
        return this.selectionCounter > 0 && this.selectionCounter != this.filteredItems.length
      },
      hasEvery() {
        return this.selectionCounter == this.filteredItems.length && this.filteredItems.length > 0
      },
    },
    watch: {
      filteredItems: {
        immediate: true,
        handler(value) {
          this.selected = makeAll(value, false)
        },
      },
    },
    methods: {
      editItem(item) {
        this.$refs.editSourceDialog.show(item)
      },
      showUnassigned() {
        this.filterToggle = 1
      },
      toggleRow(item) {
        let isSelected = this.selected[item.id]
        if (isSelected) {
          this.selectionCounter -= 1
          this.selected[item.id] = false
        } else {
          this.selectionCounter += 1
          this.selected[item.id] = true
        }
      },
      toggleAll() {
        if (this.hasEvery) {
          this.selected = makeAll(this.filteredItems, false)
          this.selectionCounter = 0
        } else {
          this.selected = makeAll(this.filteredItems, true)
          this.selectionCounter = this.filteredItems.length
        }
      },
      changeSort(sortable, column) {
        if (!sortable) return
        if (this.pagination.sortBy === column) {
          this.pagination.descending = !this.pagination.descending
        } else {
          this.pagination.sortBy = column
          this.pagination.descending = false
        }
      },
    },
  }
</script>